import React, { ChangeEvent, useEffect, useState } from "react";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import { Grid, InputLabel, MenuItem, OutlinedInput, Select } from "@material-ui/core";
import { FormattedMessage } from "react-intl";
import { SelectData } from "../models/types";

interface AddressOption {
  pid: string;
  parentPid: string;
  name: string;
  description: string;
}

interface IProps {
  formState: any;
  formErrors: any;
  language: string;
  formChange: (e: ChangeEvent<HTMLInputElement>) => any;
  setFormValue: (name: string, value: any) => any;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    inputRoot: {
      color: "inherit",
      width: "100%",
      maxWidth: "300px",
      alignSelf: "center",
    },
    inputInput: {
      padding: theme.spacing(1, 1, 1, 0),
      paddingLeft: `calc(${theme.spacing(2)}px)`,
      transition: theme.transitions.create("width"),
      width: "100%",
    },
    labelRoot: {
      color: "inherit",
      width: "100%",
      maxWidth: "300px",
      alignSelf: "center",
      paddingTop: "10px",
      paddingBottom: "5px",
      fontSize: "12px",
    },
    labelError: {
      color: "red",
      alignSelf: "center",
      fontSize: "12px",
    },
  })
);

const fetchJson = async (uri: string) => {
  const response = await fetch(process.env.REACT_APP_API_HOST + uri, {
    headers: {
      Authorization: localStorage.getItem("token") || "",
    },
  });
  const text = await response.text();
  return text ? JSON.parse(text) : [];
};

const RegisterAddressFields: React.FC<IProps> = ({
  formState,
  formErrors,
  language,
  formChange,
  setFormValue,
}) => {
  const classes = useStyles();
  const [countries, setCountries] = useState<SelectData[]>([]);
  const [cities, setCities] = useState<AddressOption[]>([]);
  const [counties, setCounties] = useState<AddressOption[]>([]);
  const [parishes, setParishes] = useState<AddressOption[]>([]);
  const [villages, setVillages] = useState<AddressOption[]>([]);
  const [streets, setStreets] = useState<AddressOption[]>([]);
  const [buildings, setBuildings] = useState<AddressOption[]>([]);
  const [apartments, setApartments] = useState<AddressOption[]>([]);

  const getSelectText = (item: SelectData) => {
    if (typeof item.value === "string") return item.value;
    return item.value?.[language] || item.value?.lv || Object.values(item.value || {}).find(Boolean) || item.code;
  };

  const selectedCountry = countries.find((country: any) => country.code === formState.country);
  const isLatvia = ((selectedCountry as any)?.country || "").toLowerCase() === "lv";

  const loadAddressOptions = async (
    endpoint: string,
    setter: (options: AddressOption[]) => void,
    parentPid?: string
  ) => {
    if (!parentPid) {
      setter([]);
      return;
    }
    const options = await fetchJson(`/users/address/${endpoint}?parentPid=${encodeURIComponent(parentPid)}`);
    setter(options);
  };

  useEffect(() => {
    fetchJson("/users/countries/preferred").then(setCountries).catch(() => setCountries([]));
  }, []);

  useEffect(() => {
    if (!formState.country && countries.length > 0) {
      setFormValue("country", countries[0].code);
    }
  }, [countries, formState.country, setFormValue]);

  useEffect(() => {
    if (isLatvia) {
      loadAddressOptions("cities", setCities, "LV");
      loadAddressOptions("counties", setCounties, "LV");
    } else {
      setCities([]);
      setCounties([]);
      setParishes([]);
      setVillages([]);
      setStreets([]);
      setBuildings([]);
      setApartments([]);
    }
  }, [isLatvia]);

  useEffect(() => {
    loadAddressOptions("parishes", setParishes, formState.countyPid);
  }, [formState.countyPid]);

  useEffect(() => {
    loadAddressOptions("villages", setVillages, formState.parishPid);
  }, [formState.parishPid]);

  useEffect(() => {
    loadAddressOptions(
      "streets",
      setStreets,
      formState.villagePid || formState.parishPid || formState.cityPid || formState.countyPid
    );
  }, [formState.villagePid, formState.parishPid, formState.cityPid, formState.countyPid]);

  useEffect(() => {
    loadAddressOptions(
      "buildings",
      setBuildings,
      formState.streetPid || formState.villagePid || formState.parishPid || formState.cityPid || formState.countyPid
    );
  }, [formState.streetPid, formState.villagePid, formState.parishPid, formState.cityPid, formState.countyPid]);

  useEffect(() => {
    loadAddressOptions("apartments", setApartments, formState.buildingPid);
  }, [formState.buildingPid]);

  useEffect(() => {
    const selectedCity = cities.find((item) => item.pid === formState.cityPid);
    const selectedCounty = counties.find((item) => item.pid === formState.countyPid);
    const selectedParish = parishes.find((item) => item.pid === formState.parishPid);
    const selectedVillage = villages.find((item) => item.pid === formState.villagePid);
    const selectedStreet = streets.find((item) => item.pid === formState.streetPid);
    const selectedBuilding = buildings.find((item) => item.pid === formState.buildingPid);
    const selectedApartment = apartments.find((item) => item.pid === formState.apartmentPid);
    const addressText =
      selectedApartment?.description ||
      selectedBuilding?.description ||
      selectedStreet?.description ||
      selectedVillage?.description ||
      formState.address ||
      "";

    setFormValue("city", selectedCity?.name || formState.city || "");
    setFormValue("cityText", selectedCity?.name || formState.cityText || formState.city || "");
    setFormValue("county", selectedCounty?.name || formState.county || "");
    setFormValue("countyText", selectedCounty?.name || formState.countyText || "");
    setFormValue("address", addressText);
    setFormValue("legalAddress", {
      country: formState.country,
      cityPid: selectedCity?.pid || "",
      cityText: selectedCity?.name || "",
      city: selectedCity?.name || "",
      countyPid: selectedCounty?.pid || "",
      countyText: selectedCounty?.name || "",
      county: selectedCounty?.name || "",
      parishPid: selectedParish?.pid || "",
      parishText: selectedParish?.name || "",
      villagePid: selectedVillage?.pid || "",
      villageText: selectedVillage?.name || "",
      streetPid: selectedStreet?.pid || "",
      streetText: selectedStreet?.name || "",
      buildingPid: selectedBuilding?.pid || "",
      buildingText: selectedBuilding?.name || "",
      apartmentPid: selectedApartment?.pid || "",
      apartmentText: selectedApartment?.name || "",
      address: addressText,
      postalCode: formState.postalCode || "",
    });
  }, [
    cities,
    counties,
    parishes,
    villages,
    streets,
    buildings,
    apartments,
    formState.country,
    formState.cityPid,
    formState.countyPid,
    formState.parishPid,
    formState.villagePid,
    formState.streetPid,
    formState.buildingPid,
    formState.apartmentPid,
    formState.postalCode,
    formState.address,
    formState.city,
    formState.cityText,
    formState.county,
    formState.countyText,
    setFormValue,
  ]);

  const handleSelect = (name: string, value: string) => {
    const resets: any = { [name]: value };
    if (name === "country") {
      resets.cityPid = "";
      resets.countyPid = "";
      resets.parishPid = "";
      resets.villagePid = "";
      resets.streetPid = "";
      resets.buildingPid = "";
      resets.apartmentPid = "";
      resets.city = "";
      resets.county = "";
      resets.address = "";
    }
    if (name === "countyPid") {
      resets.parishPid = "";
      resets.villagePid = "";
      resets.streetPid = "";
      resets.buildingPid = "";
      resets.apartmentPid = "";
      resets.county = value;
    }
    if (name === "cityPid") {
      resets.streetPid = "";
      resets.buildingPid = "";
      resets.apartmentPid = "";
      resets.city = value;
    }
    if (name === "parishPid") {
      resets.villagePid = "";
      resets.streetPid = "";
      resets.buildingPid = "";
      resets.apartmentPid = "";
    }
    if (name === "villagePid") {
      resets.streetPid = "";
      resets.buildingPid = "";
      resets.apartmentPid = "";
    }
    if (name === "streetPid") {
      resets.buildingPid = "";
      resets.apartmentPid = "";
    }
    if (name === "buildingPid") {
      resets.apartmentPid = "";
    }
    setFormValue("bulk", resets);
  };

  const renderAddressSelect = (name: string, title: string, value: string, options: AddressOption[], required = false) => (
    <>
      <InputLabel classes={{ root: classes.labelRoot }}>
        <FormattedMessage id={title} />{required ? "*" : ""}
      </InputLabel>
      <Select
        disableUnderline
        variant="outlined"
        fullWidth={true}
        value={value || ""}
        onChange={(e: any) => handleSelect(name, e.target.value)}
        classes={{ root: classes.inputInput }}
      >
        <MenuItem value=""></MenuItem>
        {options.map((option) => (
          <MenuItem key={option.pid} value={option.pid}>{option.name}</MenuItem>
        ))}
      </Select>
    </>
  );

  return (
    <>
      <InputLabel classes={{ root: classes.labelRoot }}><FormattedMessage id="register.country" />*</InputLabel>
      <Select
        disableUnderline
        variant="outlined"
        fullWidth={true}
        value={formState.country || ""}
        onChange={(e: any) => handleSelect("country", e.target.value)}
        classes={{ root: classes.inputInput }}
      >
        <MenuItem value=""></MenuItem>
        {countries.map((country) => (
          <MenuItem key={country.code} value={country.code}>{getSelectText(country)}</MenuItem>
        ))}
      </Select>
      {formErrors.country ? (<span className={classes.labelError}><FormattedMessage id={formErrors.country} /></span>) : null}

      {isLatvia ? (
        <>
          <Grid container spacing={3} direction="row">
            <Grid item xs={6}>
              {renderAddressSelect("cityPid", "register.city", formState.cityPid, cities, true)}
              {formErrors.city ? (<span className={classes.labelError}><FormattedMessage id={formErrors.city} /></span>) : null}
            </Grid>
            <Grid item xs={6}>
              {renderAddressSelect("countyPid", "register.county", formState.countyPid, counties)}
              {formErrors.county ? (<span className={classes.labelError}><FormattedMessage id={formErrors.county} /></span>) : null}
            </Grid>
          </Grid>
          {renderAddressSelect("parishPid", "register.parish", formState.parishPid, parishes)}
          {renderAddressSelect("villagePid", "register.village", formState.villagePid, villages)}
          {renderAddressSelect("streetPid", "register.street", formState.streetPid, streets)}
          {renderAddressSelect("buildingPid", "register.house", formState.buildingPid, buildings, true)}
          {formErrors.address ? (<span className={classes.labelError}><FormattedMessage id={formErrors.address} /></span>) : null}
          {renderAddressSelect("apartmentPid", "register.apartment", formState.apartmentPid, apartments)}
        </>
      ) : (
        <>
          <InputLabel classes={{ root: classes.labelRoot }}><FormattedMessage id="register.address" />*</InputLabel>
          <OutlinedInput
            value={formState.address || ""}
            name="address"
            onChange={formChange}
            classes={{ root: classes.inputRoot, input: classes.inputInput }}
          />
          {formErrors.address ? (<span className={classes.labelError}><FormattedMessage id={formErrors.address} /></span>) : null}
          <Grid container spacing={3} direction="row">
            <Grid item xs={6}>
              <InputLabel classes={{ root: classes.labelRoot }}><FormattedMessage id="register.city" />*</InputLabel>
              <OutlinedInput
                value={formState.city || ""}
                name="city"
                onChange={formChange}
                classes={{ root: classes.inputRoot, input: classes.inputInput }}
              />
              {formErrors.city ? (<span className={classes.labelError}><FormattedMessage id={formErrors.city} /></span>) : null}
            </Grid>
            <Grid item xs={6}>
              <InputLabel classes={{ root: classes.labelRoot }}><FormattedMessage id="register.county" />*</InputLabel>
              <OutlinedInput
                value={formState.county || ""}
                name="county"
                onChange={formChange}
                classes={{ root: classes.inputRoot, input: classes.inputInput }}
              />
              {formErrors.county ? (<span className={classes.labelError}><FormattedMessage id={formErrors.county} /></span>) : null}
            </Grid>
          </Grid>
        </>
      )}

      <InputLabel classes={{ root: classes.labelRoot }}><FormattedMessage id="register.index" />*</InputLabel>
      <OutlinedInput
        value={formState.postalCode || ""}
        name="postalCode"
        onChange={formChange}
        classes={{ root: classes.inputRoot, input: classes.inputInput }}
      />
      {formErrors.postalCode ? (<span className={classes.labelError}><FormattedMessage id={formErrors.postalCode} /></span>) : null}
    </>
  );
};

export default RegisterAddressFields;
